if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      if (this.successTimer) {
        clearTimeout(this.successTimer);
        this.successTimer = null;
      }
      this.submitBtn.classList.remove('is-success');

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      const variantId = Number(formData.get('id'));
      const requestedQuantity = Number(formData.get('quantity')) || 1;
      const oldCartSnapshot = ProductForm.getRenderedCartItem(variantId);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let addSucceeded = false;

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        addSucceeded = true;

        const quickAddDrawer = this.closest('quick-add-drawer');

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          if (quickAddDrawer) quickAddDrawer.addedToCart();
        }

        let itemInOldCart = oldCartSnapshot.item;
        if (!oldCartSnapshot.available) {
          const inferredOldQuantity = Math.max(0, Number(data.quantity) - requestedQuantity);
          if (inferredOldQuantity > 0) {
            itemInOldCart = { quantity: inferredOldQuantity };
          }
        }

        // Check if product was already in the cart
        if (itemInOldCart) {
          const oldQuantity = Number.isFinite(itemInOldCart.quantity)
            ? itemInOldCart.quantity
            : Math.max(0, Number(data.quantity) - requestedQuantity);

          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity,
              newQuantity: (oldQuantity === data.quantity) ? oldQuantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }

        if (theme.settings.afterAtc !== 'page') {
          // Update cart drawer contents as soon as Shopify returns the successful add response.
          if (this.cartDrawer) {
            this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refresh();
              }
            }
          }
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.removeAttribute('aria-disabled');

        if (addSucceeded) {
          this.submitBtn.classList.add('is-success');
          this.successTimer = setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
            this.successTimer = null;
          }, 1400);
        } else {
          this.submitBtn.classList.remove('is-loading');
        }
      }
    }

    /**
     * Gets the current rendered cart state for a variant before it is added.
     * This avoids a blocking /cart.js request while preserving cart event classification.
     * @param {number} variantId - Variant ID being added.
     * @returns {object} Snapshot availability and matching cart item, if present.
     */
    static getRenderedCartItem(variantId) {
      const cartItems = document.querySelector('cart-items');
      if (!cartItems) return { available: false, item: null };

      const cartItem = Array.from(cartItems.querySelectorAll('.cart-item')).find(
        (item) => Number(item.dataset.variantId) === variantId
      );
      if (!cartItem) return { available: true, item: null };

      const quantityInput = cartItem.querySelector('.qty-input__input');
      const quantity = Number(quantityInput?.dataset.initialValue ?? quantityInput?.value);

      return {
        available: true,
        item: {
          quantity: Number.isFinite(quantity) ? quantity : null
        }
      };
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
